import React from "react";
import { Box, Flex, IconButton, Image, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours, qoriaColours } from "@/theme/colours";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { BlockAllowToggle } from "./BlockAllowToggle";
import { Rule, RuleAction } from "../types";
import { formatWebsiteTarget } from "../utils";

interface RuleRowProps {
	rule: Rule;
	isFirst: boolean;
	onChangeAction: (action: RuleAction) => void;
	onDelete: () => void;
}

function useRuleTargetDisplay(rule: Rule): {
	label: string;
	subtitleKey: "rulesLibrary.form.website" | "rulesLibrary.form.signature" | "rulesLibrary.form.category";
	favicon?: string;
} {
	const { target, type } = rule;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const getFavicon = useSignatureStore((state) => state.getFavicon);
	const findSignature = useSignatureStore((state) => state.findSignature);

	const signature = findSignature(target);
	if (type === "SIGNATURE" || signature) {
		return {
			label: lookupName(target),
			subtitleKey: signature?.is_category || signature?.is_sub_category ? "rulesLibrary.form.category" : "rulesLibrary.form.signature",
			favicon: signature ? getFavicon(target) : undefined,
		};
	}

	return {
		label: formatWebsiteTarget(target),
		subtitleKey: "rulesLibrary.form.website",
	};
}

export function RuleRow(props: RuleRowProps): JSX.Element {
	const { rule, isFirst, onChangeAction, onDelete } = props;
	const { t } = useTranslation();
	const display = useRuleTargetDisplay(rule);
	const blocked = rule.action === "block";
	const isUrl = rule.type === "URL";

	return (
		<Flex align="center" gap={3} px={4} py={2} borderTop={isFirst ? undefined : "1px solid"} borderColor={qoriaColours.brand.neutral[50]}>
			<Flex
				align="center"
				justify="center"
				boxSize={8}
				rounded="lg"
				bg={isUrl ? (blocked ? qoriaColours.brand.danger[10] : qoriaColours.brand.success[10]) : undefined}
				flexShrink={0}
			>
				{display.favicon ? (
					<Image src={display.favicon} alt="" boxSize={5} />
				) : (
					<ChakraFAIcon
						icon="fa-earth-americas"
						variant="solid"
						color={isUrl ? (blocked ? qoriaColours.brand.danger[500] : qoriaColours.brand.success[500]) : colours.text.regular}
						boxSize={4}
					/>
				)}
			</Flex>
			<Box flex={1} minW={0}>
				<Text color={colours.text.title} fontWeight={400} isTruncated title={display.label}>
					{display.label}
				</Text>
				<Text fontSize="sm" color={colours.text.regular}>
					{t(display.subtitleKey)}
				</Text>
			</Box>
			<Box width="180px" flexShrink={0}>
				<BlockAllowToggle size="sm" value={rule.action} onChange={onChangeAction} />
			</Box>
			<IconButton
				aria-label={t("rulesLibrary.form.deleteRuleAria", { target: display.label })}
				variant="ghost"
				flexShrink={0}
				onClick={onDelete}
				icon={<ChakraFAIcon icon="fa-trash-can" variant="regular" color={qoriaColours.brand.danger[500]} boxSize={4} />}
			/>
		</Flex>
	);
}
